/* eslint-disable react-hooks/rules-of-hooks */
import { cloneElement, Fragment } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import _chunk from 'lodash/chunk';
import _isEmpty from 'lodash/isEmpty';

import { computeString, dateValid, filterResumeSocials, generateKeyForList } from 'utils';

import { RB_DATA, RB_DATA_FIELD } from '../info';
import { STEP_INDICES } from '../steps/schema';

export const getRecentWorkHistory = (workHistories = []) => {
  let recentWorkHistory = workHistories[0];
  let latestDate = null;
  for (let workHistory of workHistories) {
    if (workHistory['WORK_HISTORY_IS_CURRENT']) {
      recentWorkHistory = workHistory;
      break;
    } else {
      let endDate = workHistory['WORK_HISTORY_END_DATE'];
      if (endDate) {
        const [month, year] = endDate.split('/');
        const formattedDate = dayjs(`${year}-${month}`).format();
        if (!latestDate) {
          latestDate = formattedDate;
        } else if (formattedDate > latestDate) {
          latestDate = formattedDate;
          recentWorkHistory = workHistory;
        }
      }
    }
  }
  return recentWorkHistory;
};

export const getMostRecentJobTitle = (workHistories = []) => {
  const mostRecentWorkHistory = getRecentWorkHistory(workHistories);
  if (!_isEmpty(mostRecentWorkHistory)) {
    const jobTitle = mostRecentWorkHistory[RB_DATA_FIELD.WORK_HISTORY.JOB_TITLE];
    return jobTitle?.zippiaTitle || jobTitle?.title || '';
  } else {
    return '';
  }
};

export const getHeaderJobTitle = (workHistory, headline = '') => {
  if (headline) return headline;
  if (workHistory.length > 1) {
    const possibleJobs = workHistory.filter((job) => job.isCurrent);

    if (possibleJobs.length) {
      const result = possibleJobs[0];
      return result.title || result?.jobTitle?.title;
    }
  }

  return workHistory.length !== 0 && (workHistory[0].title || workHistory[0]?.jobTitle?.title);
};

export const addPStyles = (bodyHtml) => {
  if (!bodyHtml) {
    return bodyHtml;
  }
  let bodyParagraphs = bodyHtml.split('</p>');
  let result = [];
  if (bodyParagraphs && bodyParagraphs.length) {
    result = bodyParagraphs.map(function (paragraph) {
      return paragraph.replace('<p>', '<p style="orphans:3; widows:3;">');
    });
  }
  return result && result.length ? result.join('</p>') : bodyHtml;
};

export function formatDate(dateString = '') {
  if (!dateString) return;
  const splits = dateString.split('/');
  if (splits.length === 1) {
    return splits[0];
  }
  const date = new Date([splits[0], 10, splits[1]].join('/'));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export const computeDateString = (start, end, separator) => {
  const startDate = start.includes('/') ? formatDate(start) : start;
  const endDate = typeof end === 'string' ? (end.includes('/') ? formatDate(end) : end) : 'Present';

  return computeString(startDate, { [separator]: start && endDate }, endDate);
};

export const builderUnified = {
  contactInfo(data, workHistory, summary) {
    const { phone, email, firstName, lastName, location, zipCode } = data;

    return {
      phone,
      email,
      username: computeString(firstName, lastName),
      headline: getHeaderJobTitle(workHistory, summary?.headline),
      address: computeString(location?.displayValue, zipCode),
      socials: filterResumeSocials(data),
    };
  },
  workHistory(data, dateFormat = 'MMMM YYYY', options, separator = '-') {
    if (!data?.length) return [];

    return data
      .map((item) => {
        const {
          dataKey = '',
          jobTitle,
          description,
          employer,
          startDate,
          endDate,
          isCurrent,
          location,
          order,
        } = item;

        const customWorkHistory = options?.workHistory?.(item) || {};

        return {
          title: jobTitle?.title || '',
          subtitle: [employer?.displayValue || '', location?.displayValue || ''].filter(Boolean),
          description,
          date: computeDateString(startDate, isCurrent || endDate, separator, dateFormat),
          dataKey,
          order,
          ...customWorkHistory,
        };
      })
      .sort((a, b) => a.order - b.order);
  },
  education(data, dateFormat = 'MMMM YYYY', options, separator = '-') {
    if (!data?.length) return [];

    return data
      .map((item) => {
        const {
          schoolLocation,
          schoolName,
          degree,
          fieldOfStudy,
          graduationStartDate,
          graduationEndDate,
          isCurrent,
          summary,
          order,
        } = item;

        const customEducation = options?.education?.(item) || {};

        return {
          title: computeString(degree, fieldOfStudy),
          subtitle: [schoolName, schoolLocation?.displayValue].filter(Boolean),
          date: computeDateString(
            graduationStartDate,
            isCurrent || graduationEndDate,
            separator,
            dateFormat
          ),
          description: summary,
          order,
          ...customEducation,
        };
      })
      .sort((a, b) => a.order - b.order);
  },
  skill(data) {
    if (!data?.length) return [];

    return data.reduce((arr, { value }) => {
      if (!value?.skillName) return arr;

      return [...arr, value.skillName];
    }, []);
  },
  language(data) {
    if (!data?.length) return [];

    return data.reduce((arr, { value }) => {
      if (!value?.languageName) return arr;

      return [...arr, value.languageName];
    }, []);
  },
};

export const getWorkHistory = (data, dateFormat = 'MMMM YYYY', separator = '-') => {
  if (!data?.length) return [];

  return data.map(
    ({
      dataKey = '',
      jobTitle,
      description,
      employer,
      startDate,
      endDate,
      isCurrent,
      location,
      order,
    }) => ({
      title: jobTitle?.title || '',
      location: location?.displayValue || '',
      employer: employer?.displayValue || '',
      description,
      date: computeDateString(startDate, isCurrent || endDate, separator, dateFormat),
      dataKey,
      order,
    })
  );
};

export const getEducations = (
  data,
  dateFormat = 'MMMM YYYY',
  separator = '-',
  appendDegree = false
) => {
  if (!data?.length) return [];

  return data.map(
    ({
      schoolLocation,
      schoolName,
      degree,
      fieldOfStudy,
      graduationStartDate,
      graduationEndDate,
      isCurrent,
      summary,
      order,
    }) => {
      const location = schoolLocation?.displayValue || '';

      const formattedDegree =
        degree &&
        appendDegree &&
        ['High School', ' Degree'].every((subStr) => !degree.includes(subStr))
          ? `${degree} Degree`
          : `${degree || ''}`;

      let title = formattedDegree;

      fieldOfStudy && (title += ` ${fieldOfStudy}`);

      return {
        title,
        schoolName,
        location,
        date: computeDateString(
          graduationStartDate,
          isCurrent || graduationEndDate,
          separator,
          dateFormat
        ),
        summary,
        degree: formattedDegree,
        fieldOfStudy,
        order,
      };
    }
  );
};

export const getCertifications = (data) => {
  if (!data?.length) return [];

  return data.map(({ value, issueDate }) => ({
    name: value?.certificationName || '',
    date: formatDate(issueDate) || '',
  }));
};

export const getLanguages = (data) => {
  if (!data?.length) return [];

  return data.reduce((arr, { value }) => {
    if (value?.languageName) return [...arr, { language: value?.languageName }];
    return arr;
  }, []);
};

export const getSkills = (data) => {
  if (!data?.length) return [];

  return data.reduce((arr, { value }) => {
    if (value?.skillName) return [...arr, { skill: value?.skillName }];

    return arr;
  }, []);
};

export const cleanWorkHistory = (data, deleteFunc) => {
  const workHistoryData = {};

  Object.keys(data).forEach((key) => {
    const FIELDS = RB_DATA_FIELD.WORK_HISTORY;
    const item = data[key];

    const hasTitle = !!item?.[FIELDS.JOB_TITLE]?.title;
    const hasEndDate = !!item?.[FIELDS.END_DATE];
    const hasStartDate = !!item?.[FIELDS.START_DATE];
    const hasLocation = !!item?.[FIELDS.LOCATION]?.displayValue;
    const hasEmployer = !!item?.[FIELDS.EMPLOYER]?.displayValue;

    if (hasTitle || hasEndDate || hasStartDate || hasLocation || hasEmployer) {
      workHistoryData[key] = item;
    } else {
      deleteFunc({ dataName: RB_DATA.WORK_HISTORY, dataKey: key });
    }
  });

  return workHistoryData;
};

export const cleanEducation = (data, deleteFunc) => {
  const FIELDS = RB_DATA_FIELD.EDUCATION;
  const educationData = {};

  Object.keys(data).forEach((key) => {
    const item = data[key];

    const hasDegree = !!item?.[FIELDS.DEGREE];
    const hasFOS = !!item?.[FIELDS.FIELD_OF_STUDY]; // Field of study
    const hasEndDate = !!item?.[FIELDS.GRADUATION_END_DATE];
    const hasStartDate = !!item?.[FIELDS.GRADUATION_START_DATE];
    const hasLocation = !!item?.[FIELDS.SCHOOL_LOCATION]?.displayValue;
    const hasSchoolName = !!item?.[FIELDS.SCHOOL_NAME];

    if (hasDegree || hasFOS || hasEndDate || hasStartDate || hasLocation || hasSchoolName) {
      educationData[key] = item;
    } else {
      deleteFunc({ dataName: RB_DATA.EDUCATION, dataKey: key });
    }
  });

  return educationData;
};

export const isDateValid = (date) => {
  return dateValid(date, 'M/YYYY') || dateValid(date, 'YYYY');
};

function getResumeHighlightStyles(stepIndex, subStepIndices) {
  const { currentStepIndex, currentSubStepIndex } = useSelector(
    ({ resumeBuilder }) => resumeBuilder.flow
  );
  const isPreviewOpen = useSelector(({ resumeBuilder }) => resumeBuilder.previewModalInfo.open);
  const isPreviewStep =
    window?.location?.href?.includes('step=resume-preview') ||
    window?.location?.pathname === '/my-resume/';

  if (
    !isPreviewStep &&
    !isPreviewOpen &&
    stepIndex === currentStepIndex &&
    subStepIndices.includes(currentSubStepIndex)
  ) {
    return 'highlight-current-section';
  } else {
    return '';
  }
}

export const getSectionHighlightClasses = (dynamicSubStepIndex) => ({
  headerClass: getResumeHighlightStyles(STEP_INDICES.CONTACT, [0]),
  workHistoryClass: getResumeHighlightStyles(STEP_INDICES.WORK_HISTORY, [0, 1]),
  educationClass: getResumeHighlightStyles(STEP_INDICES.EDUCATION, [0, 1]),
  skillsClass: getResumeHighlightStyles(STEP_INDICES.SKILLS, [0]),
  summaryClass: getResumeHighlightStyles(STEP_INDICES.SUMMARY, [0]),
  certClass: getResumeHighlightStyles(STEP_INDICES.INTERESTS, [1]),
  languageClass: getResumeHighlightStyles(STEP_INDICES.INTERESTS, [2]),
  customSectionClass: getResumeHighlightStyles(STEP_INDICES.INTERESTS, [dynamicSubStepIndex]),
});

export const getDescription = (item = {}) => item?.description || item?.summary;

export const cloning = (component, list = [], options = {}) => {
  const {
    isUL = false,
    renderIsFirst = false,
    printMode = false,
    descriptionComponent,
    title,
    name,
  } = options;

  const sliceStart = printMode ? (isUL ? 2 : 1) : 0;
  const slicedLen = list.length - sliceStart - 1;

  const printSingle = () => {
    const item = list?.[0];

    if (!item) return null;

    return cloneElement(component, {
      ...item,
      isFirst: true,
      printItem: true,
      isLast: list?.length === 1,
    });
  };

  const printDuo = () =>
    list?.slice(0, 2)?.map((item, index) => (
      <Fragment key={`template-${name}-${title}-item-${index}`}>
        {cloneElement(component, {
          ...item,
          isFirst: index === 0,
          printItem: true,
          isLast: index === 1,
        })}
      </Fragment>
    )) || null;

  const printDescription = () => {
    const description = getDescription(list?.[0]);

    if (!description) return null;

    return cloneElement(descriptionComponent, { description, printItem: true });
  };

  const renderMulti = () =>
    list?.slice(sliceStart)?.map((item, index, arr) => (
      <Fragment key={`template-${name}-${title}-item-${index + sliceStart}`}>
        {cloneElement(component, {
          ...item,
          isFirst: index === 0 && sliceStart === 0 && (renderIsFirst || !printMode),
          isLast: index === slicedLen,
          isRest: true,
          isHalf: Math.ceil(arr.length / 2) === index,
        })}
      </Fragment>
    )) || null;

  const renderColList = (cols = 1) => {
    const step = Math.ceil(list.length / cols);
    const parts = _chunk(list, step);

    return parts.map((part, index) => (
      <ul key={generateKeyForList(index, name, title, sliceStart)}>
        {part.map((item, idx) => (
          <Fragment key={`template-${name}-${title}-item-${idx + sliceStart}`}>
            {cloneElement(component, item)}
          </Fragment>
        ))}
      </ul>
    ));
  };

  return {
    printSingle,
    printDuo,
    printDescription,
    renderMulti,
    renderColList,
  };
};

const getFontSettings = (size, toReduce) => ({
  fontSize: `calc(${size} - ${toReduce}px)`,
  lineHeight: 1.15,
});

export const computeHeaderStyles = (content, options) => {
  const { baseFontSize = '2.6em', baseLength = 6, step = 1.1, maxReduce = 20 } = options;

  const longest = Math.max(...content.split(' ').map((word) => word.length));
  const toReduce = baseLength < longest ? longest * step : 0;

  return getFontSettings(baseFontSize, maxReduce > toReduce ? toReduce : maxReduce);
};
